import { Grid, styled, useMediaQuery, useTheme } from '@mui/material';
import {
  FunctionComponent,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PbStepperImage from '../components/pbStepperImage/pbStepImage';
import { constants } from '../constants';
import LazyLoadingErrorBoundary from '../core/componentLoading/lazyLoadingErrorBoundary';
import LoadingComponent from '../core/componentLoading/loading';
import ComponentBuilder from '../core/formBuilder/componentBuilder';
import Element, { FormElement } from '../core/formBuilder/formularElement';
import MetaDataContext from '../core/formBuilder/metaDataContext';
import PageStepperContextProvider from '../core/formBuilder/pageStepperContext';
import { DataSchemaContext } from '../core/validation/dataSchemaContext';
import desktopImageAmex from '../imgs/desktop/Amex_Antragsstrecke_Desktop.jpg';
import desktopImageVisa from '../imgs/desktop/Visa_Antragsstrecke_Desktop_1500x2000px.jpg';
import mobileImageAmex from '../imgs/mobile/Amex_Antragsstrecke_Visual_Handy.jpg';
import mobileImageVisa from '../imgs/mobile/Visa_Antragsstrecke_Visual_Handy_1200x201px.jpg';
import desktopImageAmexVers from '../imgs/desktop/PCH-15113_Antragsstrecke_Visual_Desktop_Tablet_mitKarte_1500x2000.jpg';
import mobileImageAmexVers from '../imgs/mobile/PCH-15113_Antragsstrecke_Visual_Mobile_mitKarte_1200x201.jpg';
import { enableLegacyTwoColumnLayout } from '../core/services/legacyTwoColumnLayoutService';
import { collectRoutes, isValidRoute } from './pageUtils';
import { assign, mergeWith } from 'lodash';

const PREFIX = 'FormPage';

const classes = {
  desktopScrollcontainer: `${PREFIX}-desktopScrollcontainer`,
};

export const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.desktopScrollcontainer}`]: {},
}));

interface FormPageProps {
  config: {
    form: {} | FormElement;
    meta: {};
    dataSchema: {
      errorSchema: Record<string, Record<string, string>>;
      validationSchema: {};
    };
    thirdPartySystems: {};
  };
}

export const FormPage: FunctionComponent<FormPageProps> = ({ config }) => {
  const theme = useTheme();
  const { project } = useParams();
  const MEDIAQUERY_smUP = useMediaQuery(theme.breakpoints.up('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  // Workaround to display CustomErrorPage due to wildcard FormPage matcher in ProjectPage
  const formRoutes = collectRoutes((config?.form as FormElement)?.elements);
  const [validationSchema, setValidationSchema] = useState(
    config.dataSchema.validationSchema ?? {}
  );
  const [errorSchemaOverwrites, setErrorSchemaOverwrites] = useState<
    Record<string, string>[]
  >([]);

  useEffect(() => {
    if (project) {
      if (!isValidRoute(project, location, formRoutes)) {
        navigate(`/${project}/error`, { state: { statusCode: 404 } });
      }
    }
  }, [project, location, formRoutes, navigate]);

  const overrideValidationSchema = useCallback(
    (override: Record<string, any>) => {
      setValidationSchema((current: any) => {
        if (override.properties) {
          return {
            ...current,
            // replace only the overridden fields
            properties: assign({}, current.properties, override.properties),
          };
        }
        return { ...current, ...override };
      });
    },
    []
  );

  const overrideErrorSchema = useCallback(
    (override: Record<string, string>) => {
      setErrorSchemaOverwrites((current) => [...current, override]);
    },
    []
  );

  const overridenErrorSchema = useMemo(() => {
    return errorSchemaOverwrites.reduce((result, current) => {
      return mergeWith({}, result, current, (objVal, srcVal) =>
        Array.isArray(objVal) ? srcVal : undefined
      );
    }, config.dataSchema.errorSchema);
  }, [config.dataSchema.errorSchema, errorSchemaOverwrites]);

  return (
    <PageStepperContextProvider>
      <DataSchemaContext.Provider
        value={{
          validationSchema,
          errorSchema: overridenErrorSchema,
          overrideValidationSchema,
          overrideErrorSchema,
        }}
      >
        <MetaDataContext.Provider value={{ meta: config.meta }}>
          <Grid
            style={{
              overflowX: 'clip',
              width: '100%',
            }}
            container
            alignItems={constants.LAYOUT === 'SINGLE' ? 'center' : ''}
            justifyContent={constants.LAYOUT === 'SINGLE' ? 'center' : ''}
          >
            {constants.LAYOUT === 'DOUBLE' &&
              enableLegacyTwoColumnLayout(config) && (
                <PbStepperImage
                  desktopImage={
                    project === 'amex' || project === 'amexv2'
                      ? desktopImageAmex
                      : project === 'amexVers'
                      ? desktopImageAmexVers
                      : desktopImageVisa
                  }
                  mobileImage={
                    project === 'amex' || project === 'amexv2'
                      ? mobileImageAmex
                      : project === 'amexVers'
                      ? mobileImageAmexVers
                      : mobileImageVisa
                  }
                  objectPosition={
                    project === 'amex' || project === 'amexv2'
                      ? 'left center'
                      : 'top center'
                  }
                />
              )}
            <Grid
              style={{ overflowX: 'clip', width: '100%', padding: '0' }}
              item
              sm
              lg
              className={MEDIAQUERY_smUP ? classes.desktopScrollcontainer : ''}
            >
              <LazyLoadingErrorBoundary
                debug={(window as any)._env_.DEBUG?.toLowerCase() === 'true'}
              >
                <Suspense fallback={<LoadingComponent />}>
                  <ComponentBuilder
                    element={config.form as Element}
                    formThirdPartySystemsConfig={config?.thirdPartySystems}
                  />
                </Suspense>
              </LazyLoadingErrorBoundary>
            </Grid>
          </Grid>
        </MetaDataContext.Provider>
      </DataSchemaContext.Provider>
    </PageStepperContextProvider>
  );
};
